import { useEffect, useRef, useState } from "react";
import type { Agent } from "./fixtures";
import { useSetAgentEnabled, useDeleteAgent } from "./useAgentsUi";

/**
 * Per-agent overflow menu. A 3-dot trigger opens a small popover with two
 * options: the active/paused toggle slider and a Delete action that removes the
 * agent. Replaces the bare toggle slider in the roster card and detail header.
 *
 * `onDeleted` lets the caller react to a successful delete (e.g. the detail
 * view clears its scope back to the roster, since the agent no longer exists).
 */
export function AgentMenu({
  agent,
  onDeleted,
}: {
  agent: Agent;
  onDeleted?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const setEnabled = useSetAgentEnabled();
  const deleteAgent = useDeleteAgent();
  const on = agent.status === "active";

  useEffect(() => {
    if (!open) return;
    const onDocPointer = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocPointer);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocPointer);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div
      className="agui-menu"
      ref={rootRef}
      // The roster card is itself a button; keep menu clicks from opening it.
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className="agui-menu__trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Agent options"
        onClick={() => setOpen((o) => !o)}
      >
        ⋯
      </button>
      {open ? (
        <div className="agui-menu__pop" role="menu">
          <div className="agui-menu__row">
            <span className="agui-menu__label">{on ? "Active" : "Paused"}</span>
            <button
              type="button"
              role="switch"
              aria-checked={on}
              aria-label={on ? "Pause agent" : "Activate agent"}
              className={`agui-toggle agui-toggle--sm${on ? " is-on" : ""}`}
              disabled={setEnabled.isPending}
              onClick={() =>
                setEnabled.mutate({ id: agent.id, enabled: !on })
              }
            >
              <span className="agui-toggle__knob" />
            </button>
          </div>
          <button
            type="button"
            role="menuitem"
            className="agui-menu__delete"
            disabled={deleteAgent.isPending}
            onClick={() =>
              deleteAgent.mutate(agent.id, {
                onSuccess: () => {
                  setOpen(false);
                  onDeleted?.();
                },
              })
            }
          >
            Delete agent
          </button>
        </div>
      ) : null}
    </div>
  );
}
